import { API_SERVICEPROVIDERS_BACKEND, API_USERS, API_USERS_BACKEND } from "../../../../api/api";
import { selectClientID } from "../../../../context/slices/loggedInClientSlice";
import { selectStateInUS } from "../../../../context/slices/stateInUsSlice";
import { thankYouModalActions } from "../../../../context/slices/thankYouModalSlice";
import { ACTION_addProviderToProxyCaseload, selectLoggedInClientAssignment, selectUser } from "../../../../context/slices/userProfileSlice";
import { useXNGDispatch, useXNGSelector } from "../../../../context/store";
import { PostAccessRequest, ServiceProviderRef, UserRef } from "../../../../profile-sdk";
import { CaseloadType } from "@xng/xng-backend";
import refetchUserAndUnpostedSessions from "../../../../views/unposted_sessions/utils/refetch_user_and_unposted_sessions";

export function useAddProviderToProxyCaseload() {
  const dispatch = useXNGDispatch();
  const clientID = useXNGSelector(selectClientID);
  const stateInUS = useXNGSelector(selectStateInUS);
  const loggedInClientAssignment = useXNGSelector(selectLoggedInClientAssignment);
  const user = useXNGSelector(selectUser);

  async function addToProxyCaseload(serviceProvider: ServiceProviderRef) {
    const spId = loggedInClientAssignment.serviceProviderProfile?.id;
    if (!serviceProvider?.id || !clientID || !spId) {
      console.error("Insufficient data provided to 'addProviderToProxyCaseload'!");
      return;
    }

    const memberUser = await API_USERS_BACKEND
      .getUserByServiceProvider({
        serviceProviderId: serviceProvider.id,
        clientId: clientID,
      });
    if (!memberUser.id) throw new Error("Could not resolve user for service provider");

    await API_SERVICEPROVIDERS_BACKEND
      .postCaseload({
        serviceProviderId: spId,
        caseloadType: CaseloadType.NUMBER_2,
        postCaseloadBody: { memberId: memberUser.id },
      });

    // Optimistically update Redux with the new provider
    dispatch(
      ACTION_addProviderToProxyCaseload({
        loggedinUserId: clientID,
        provider: serviceProvider,
        state: stateInUS,
      }),
    );

    // Request post access for the proxy provider (response may be empty — ignore parse errors)
    const postAccessRequest: PostAccessRequest = {
      requestedServiceProvider: serviceProvider,
      requestingUser: {
        id: user?.id,
        firstName: user?.firstName,
        lastName: user?.lastName,
        email: user?.emailAddress,
      } as UserRef,
    };
    try {
      await API_USERS.v1UsersRequestProxyAccessToPostPost(stateInUS, postAccessRequest);
    } catch {
      // Endpoint returns empty Ok() which the legacy SDK can't parse — safe to ignore
    }

    refetchUserAndUnpostedSessions();
    dispatch(
      thankYouModalActions.ACTION_ShowThankyouModal({
        show: true,
        text: `You can now access ${serviceProvider.firstName} ${serviceProvider.lastName}'s account 
      by navigating to “My Profile” in the 
      navigation bar. Access to post in their 
      account has been requested.`,
      }),
    );
  }

  return addToProxyCaseload;
}
